import {Price} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//price.types';
import {Badge} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/badge.types';
import {
  SizeSelection_Size,
  SizeSelection_SizeRuns_SizeRun,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {useFormatCurrency} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
import {
  BadgeKind,
  COLORS,
  mediaTabletAndUp,
  mobileOnly,
} from '@aboutyou/ui-components';
import {useMemo} from 'react';
import {css} from 'styled-components';

const normalizePrice = (price: Price | undefined) => {
  if (!price) {
    return {
      unformattedFinalPrice: undefined,
      currencyCode: undefined,
      reductions: [],
    };
  }

  const unformattedFinalPrice = price.price.amount;
  const currencyCode = price.price.currencyCode;

  return {
    unformattedFinalPrice,
    currencyCode,
    reductions: price.reductions,
  };
};

export const normalizeSizeRuns = (
  sizeRuns: Array<SizeSelection_SizeRuns_SizeRun>,
) => {
  return sizeRuns.map(sizeRun => ({
    label: sizeRun.label,
    value: sizeRun.label,
  }));
};

export const useNormalizedSizes = (
  sizes: SizeSelection_Size[],
  options?: {
    enableYourSizeGreyBadges?: boolean;
    disableSoldOut?: boolean;
    enablePriceOverhaulV2?: boolean;
  },
) => {
  const formatCurrency = useFormatCurrency();

  const badgeOptions = {
    sale: null,
    campaignSale: null,
    enableYourSizeGreyBadges: options?.enableYourSizeGreyBadges || false,
  };

  return useMemo(() => {
    return sizes.filter(Boolean).map(size => {
      const {availability, label, leftBadges, rightBadges, sizeId, subLabel} =
        size;

      const indexOfBestPriceBadge = leftBadges.findIndex(
        badge => badge.type?.$case === 'bestPrice',
      );

      const copyOfLeftBadges = [...leftBadges];
      const bottomBadges =
        indexOfBestPriceBadge > -1
          ? copyOfLeftBadges.splice(indexOfBestPriceBadge, 1)
          : [];

      const filterSaleAndCampaignBadges = (badge: Badge) =>
        badge.type?.$case !== 'sale' && badge.type?.$case !== 'campaignSale';

      const filteredCopyOfLeftBadges = copyOfLeftBadges.filter(
        filterSaleAndCampaignBadges,
      );
      const filteredRightBadges = rightBadges.filter(
        filterSaleAndCampaignBadges,
      );
      const filteredBottomBadges = bottomBadges.filter(
        filterSaleAndCampaignBadges,
      );

      if (availability?.$case === 'soldOut') {
        return {
          badgesListPositionStart: getDisplayBadges(
            filteredCopyOfLeftBadges,
            badgeOptions,
          ),
          badgesListPositionEnd: getDisplayBadges(
            filteredRightBadges,
            badgeOptions,
          ),
          badgesListPositionBottom: getDisplayBadges(
            filteredBottomBadges,
            badgeOptions,
          ),
          id: sizeId,
          label,
          sale: false,
          selected: false,
          soldOut: !options?.disableSoldOut,
          subLabel,
        };
      }

      if (availability?.$case === 'inStock') {
        const {unformattedFinalPrice, currencyCode, reductions} =
          normalizePrice(availability.inStock.price);

        return {
          badgesListPositionStart: getDisplayBadges(
            filteredCopyOfLeftBadges,
            badgeOptions,
          ),
          badgesListPositionEnd: getDisplayBadges(
            filteredRightBadges,
            badgeOptions,
          ),
          badgesListPositionBottom: getDisplayBadges(
            filteredBottomBadges,
            badgeOptions,
          ),
          formattedFinalPrice: unformattedFinalPrice
            ? formatCurrency(unformattedFinalPrice, currencyCode)
            : undefined,
          id: sizeId,
          reductions,
          label,
          price: options?.enablePriceOverhaulV2
            ? availability.inStock.priceV2
            : availability.inStock.price,
          sale: options?.enablePriceOverhaulV2
            ? Boolean(availability.inStock.priceV2.badge[0])
            : Boolean(availability.inStock.price?.reductions[0]),
          soldOut: false,
          subLabel,
        };
      }

      Log.warn(
        '@aboutyou/logic-components',
        `Expected size to be of type *soldOut* or *inStock* but received unhandled case`,
        size,
      );

      return {
        id: sizeId,
        label,
        selected: false,
        subLabel,
      };
    });
  }, [formatCurrency, sizes]);
};

const normalizeBadge = (
  badge: Badge,
  options?: {
    sale: string | null;
    campaignSale: string | null;
    enableYourSizeGreyBadges?: boolean;
  },
): BadgeKind | undefined => {
  switch (badge.type?.$case) {
    case 'bestPrice':
      return {
        kind: 'BEST_PRICE',
        label: badge.type.bestPrice.label,
        untranslatedLabel: '',
      };

    case 'campaignSale': {
      return options?.campaignSale
        ? {
            kind: 'CAMPAIGN_SALE',
            color: badge.type.campaignSale.campaignColor,
            label: options.campaignSale,
            untranslatedLabel: '',
          }
        : undefined;
    }

    case 'sale': {
      return options?.sale
        ? {
            kind: 'SALE',
            label: options.sale,
            untranslatedLabel: '',
          }
        : undefined;
    }

    case 'sizeFinderFit':
    case 'yourSize':
      return {
        kind: 'YOUR_SIZE',
      };

    default:
      return undefined;
  }
};

export const getDisplayBadges = (
  badges: Array<Badge>,
  options?: {
    sale: string | null;
    campaignSale: string | null;
    enableYourSizeGreyBadges?: boolean;
  },
) => {
  return badges.reduce<BadgeKind[]>((resultBadges, badge) => {
    const normalizedBadge = normalizeBadge(badge, options);

    if (normalizedBadge) {
      return [...resultBadges, normalizedBadge];
    }

    return resultBadges;
  }, []);
};

export const sharedSizeListStyles = css`
  ${mobileOnly(
    css`
      border-top: 1px solid ${COLORS.Grey4};
    `,
  )}

  padding: 0.5rem 0;
  overflow-y: scroll;

  @media ${mediaTabletAndUp} {
    max-height: 240px;
  }

  > * {
    border: none;

    &:last-child {
      border: none;
    }
  }
`;
